<template>
  <div class="app-container">
    <Header :show-scroll-progress="true" />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <BlogHeader
          :title="title"
          :subheading="subheading"
          :date="date"
          :author="author"
          :author-avatar="authorAvatar"
          :reading-time="readingTime"
          :hero-image="heroImage"
        />

        <div class="billable-experience-blog">
          <section class="blog-section">
            <p>
              For years I've watched drone operators do the same thing: they
              show up, fly a perfectly good mission, then disappear into a black
              hole of file transfers, edits, exports, and uploads. And here's
              the funny thing — clients have quietly learned to expect exactly
              that. They expect the waiting, the downloading, the "here's the
              link" email. It's become so normalized that many DSPs don't
              question whether this workflow even makes sense anymore.
            </p>
            <p>
              But it's strange, right? The most valuable part of the job — the
              actual flight — is the only part the client never gets to see.
            </p>
            <p>
              That's where this shift started for me. I kept noticing something:
              the more real-time visibility clients had, the happier they were.
              And the less time DSPs spent doing painful post-production work.
              It's almost as if we accidentally trained clients to be passive
              receivers when, in reality, they desperately want to be in the
              room (or sky) with us.
            </p>
            <p><strong>Live access fixes that.</strong></p>
          </section>

          <section class="blog-section">
            <h2>Why this "live" thing suddenly matters</h2>
            <p>
              There's a quiet truth that nobody talks about: raw files are
              commodities. They're necessary, but they don't carry emotion. They
              don't show the decision moments. They don't carry the "wait, zoom
              in there" energy that clients love.
            </p>
            <p>
              A live session is different. It feels like participation. It
              replaces long Slack threads with "yep, that shot is perfect, move
              on." It takes jobs that would've been three rounds of revisions
              and collapses them to a single flight.
            </p>
            <p>And something interesting happens when clients feel involved:</p>
            <p class="highlight-text">
              they stop treating you like a vendor and start treating you like
              part of the project.
            </p>
            <p>
              That's worth money. Usually 15–30% more, by the way. But beyond
              the numbers, it changes the tone of the entire engagement. You're
              not the person who "sends the files." You're the partner who helps
              them make a decision in real time.
            </p>
          </section>

          <section class="blog-section">
            <h2>How operators are packaging this (intentionally or not)</h2>
            <p>
              Some DSPs slide into this quietly. They'll tack on a line item
              called "remote viewing," almost as an apology.
            </p>
            <p>
              Others — the ones who see what's coming — rebuild their offering
              around the live experience. They pitch it as a collaborative
              session, almost like a remote on-site meeting. A moment the client
              joins instead of waits for.
            </p>
            <p>
              That second group is winning. Because the moment a client joins a
              flight once, it's really hard for them to go back to the old "wait
              three days for clips" workflow. They've tasted the shortcut.
            </p>
            <p><strong>And nobody gives up shortcuts willingly.</strong></p>
          </section>

          <section class="blog-section">
            <h2>Pricing doesn't need to be complicated</h2>
            <p>
              I've seen operators overthink this. They build these monstrous
              spreadsheets with per-viewer rates, bandwidth calculations, stream
              durations — as if the client wants to compare you to AWS.
            </p>
            <p><strong>Clients don't care.</strong></p>
            <p>
              They care that they can join the flight and leave with clarity.
            </p>
            <p>Most DSPs who offer live sessions stick to one of these:</p>
            <ul class="pricing-list">
              <li>A single session fee</li>
              <li>A "team viewing" fee</li>
              <li>A bundle of monthly live minutes</li>
            </ul>
            <p>
              And because collaboration reduces rework dramatically (20–40%
              fewer revisions), these sessions pay for themselves even if you
              never charge a premium. The fact that you can charge more is just
              the upside.
            </p>
          </section>

          <section class="blog-section">
            <h2>Let's address the tech elephant</h2>
            <p>
              Yes, streaming used to be horrible. RTMP configs, VPNs, apps that
              crash, clients asking "why is it lagging," operators juggling two
              phones and a controller.
            </p>
            <p>
              <strong
                >But that's baked into people's memories, not reality.</strong
              >
            </p>
            <p>
              Skyhost basically killed that entire problem. Send a link.
              Everyone joins. They can talk, guide, record, bookmark moments —
              even if they're ten time zones away.
            </p>
            <p>
              The operator flies. The clients focus. The tech stays out of the
              way.
            </p>
            <p><strong>That's exactly how it should be.</strong></p>
            <div class="blog-image-container">
              <img
                src="@/assets/livestream_flight.png"
                class="banner"
                alt="Live streaming flight interface"
              />
            </div>
          </section>

          <section class="blog-section">
            <h2>The real magic is in the stories</h2>
            <p>
              Every DSP I've spoken to has at least one moment where live access
              saved a job:
            </p>
            <ul class="stories-list">
              <li>
                A property developer adding an investor mid-flight without
                asking you to restart the session.
              </li>
              <li>
                An engineer spotting an issue during a roof scan that would've
                required a second visit.
              </li>
              <li>
                A marketing director grabbing a perfect hero shot live and
                repurposing it instantly for a campaign.
              </li>
            </ul>
            <p>
              These aren't dramatic use cases — they're everyday flights that
              become significantly smoother when clients can simply be present.
            </p>
            <p class="highlight-text">
              <strong>Presence is the product.</strong>
            </p>
            <p class="highlight-text">
              <strong>Footage is just the souvenir.</strong>
            </p>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>So where does this leave you?</h2>
            <p>
              If you've never offered live access before, try it on one job.
              Just one. Invite the client. Let them talk. Let them guide. Notice
              how the whole dynamic changes. Notice how much faster things wrap
              up. Notice how they react afterward.
            </p>
            <p><strong>This is the shift:</strong></p>
            <p class="highlight-text">
              you're no longer selling files — you're selling the moment.
            </p>
            <p>
              And once clients experience the moment, they don't want to go
              back.
            </p>
            <p>
              Ready to transform your flights into billable live experiences?
              Get started with
              <a href="https://skyhost.ai" target="_blank" class="blog-link"
                >Skyhost</a
              >
              today.
            </p>
          </section>
        </div>

        <BlogFooter
          :tags="tags"
          :title="title"
          :current-page-url="currentPageUrl"
          :related-blogs="relatedBlogs"
          @navigate-to-blog="navigateToBlog"
          @navigate-to-blogs="navigateToBlogs"
        />
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import BlogHeader from "@/components/organisms/BlogHeader.vue";
import BlogFooter from "@/components/organisms/BlogFooter.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "BillableLiveExperienceBlog",
  components: {
    Header,
    Footer,
    BlogHeader,
    BlogFooter,
  },
  data() {
    const blogId = "billable-live-experience";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      subheading: blog.subheading,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
    readingTime() {
      // Calculate reading time: average 200 words per minute
      // Approximate word count from content sections
      const wordCount =
        this.title.split(" ").length +
        (this.excerpt?.split(" ").length || 0) +
        2500; // Approximate content words
      return Math.ceil(wordCount / 200);
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    const allOtherBlogs = Object.values(blogsRegistry).filter(
      (blog) => blog.id !== this.id
    );

    // First, try to get blogs with matching tags
    const matchingTagBlogs = allOtherBlogs.filter((blog) =>
      blog.tags.some((tag) => this.tags.includes(tag))
    );

    // If we have matching tag blogs, use them, otherwise use any other blogs
    const relatedBlogs =
      matchingTagBlogs.length > 0 ? matchingTagBlogs : allOtherBlogs;

    // Take up to 2 blogs
    this.relatedBlogs = relatedBlogs.slice(0, 2);
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}
.billable-experience-blog {
  font-family: "Charter", "Georgia", "Cambria", serif;
  line-height: 1.8;
  color: #333;
  font-size: 21px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

h3 {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 600;
  letter-spacing: -0.01em;
}

p {
  margin-bottom: 1.5rem;
  font-size: 21px;
  line-height: 1.8;
  font-family: "Charter", "Georgia", "Cambria", serif;
}

.highlight-text {
  font-size: 1.15rem;
  color: #000;
  font-weight: 500;
  margin: 1.5rem 0;
  padding-left: 1rem;
  border-left: 3px solid #1565c0;
}

.pricing-list,
.stories-list {
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
  font-family: "Charter", "Georgia", "Cambria", serif;
  font-size: 21px;
}

.pricing-list li,
.stories-list li {
  margin-bottom: 0.8rem;
  line-height: 1.8;
}

.banner {
  width: 100%;
  height: auto;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.blog-image-container {
  margin: 2rem 0;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .blog-title {
    font-size: 2rem;
  }

  .web-blog-content {
    padding: 1.5rem;
  }

  h2 {
    font-size: 1.75rem;
  }

  .highlight-text {
    font-size: 1.05rem;
    padding-left: 0.8rem;
  }
}
</style>
